import { Alert, Box, Button, Card, CardContent, MenuItem, Stack, TextField, Typography } from '@mui/material'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ApiError } from '@/api/client'
import { hasMobile, portalApi, portalSession } from './portalApi'

type SignupStart = {
  accessToken: string | null
  customerName: string | null
  mustChangePassword: boolean
  codeRequired: boolean
  challenge: string | null
  sentTo: string | null
  validMinutes: number
}

/**
 * Creating a portal account. A code goes to a mobile number that is already
 * one of ours; the code and a new password are then checked before anyone is
 * let in. A number we do not know gets the same screen but no code, so the
 * page does not show who is a customer.
 */
export function CustomerSignupPage() {
  const navigate = useNavigate()
  const [step, setStep] = useState<'number' | 'code'>('number')
  const [mobile, setMobile] = useState('')
  const [name, setName] = useState('')
  const [title, setTitle] = useState('Mr.')
  const [challenge, setChallenge] = useState('')
  const [sentTo, setSentTo] = useState('')
  const [code, setCode] = useState('')
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const fail = (problem: unknown, fallback: string) =>
    setError(problem instanceof ApiError ? problem.message : fallback)

  const sendCode = async () => {
    setBusy(true)
    setError(null)
    try {
      const result = await portalApi.post<SignupStart>('/auth/signup', { mobile })
      setChallenge(result.challenge ?? '')
      setSentTo(result.sentTo ?? 'your mobile')
      setStep('code')
    } catch (problem) {
      fail(problem, 'Something went wrong. Please try again.')
    } finally {
      setBusy(false)
    }
  }

  const finish = async () => {
    if (password.length < 8) {
      setError('The password needs at least 8 characters.')
      return
    }
    if (password !== confirm) {
      setError('The two passwords do not match.')
      return
    }
    setBusy(true)
    setError(null)
    try {
      const result = await portalApi.post<SignupStart>('/auth/signup/verify', {
        challenge,
        code: code.trim(),
        password,
        title,
        name: name.trim(),
      })
      if (result.accessToken) {
        portalSession.save(result.accessToken, result.customerName ?? '')
        navigate('/my', { replace: true })
      }
    } catch (problem) {
      fail(problem, 'That code was not accepted.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh', display: 'grid', placeItems: 'center', p: 2 }}>
      <Card sx={{ width: '100%', maxWidth: 420 }}>
        <CardContent>
          <Typography variant="h5" gutterBottom>
            Create your account
          </Typography>

          {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}

          {step === 'number' ? (
            <Stack spacing={2} component="form" onSubmit={(event) => {
              event.preventDefault()
              if (hasMobile(mobile) && name.trim()) void sendCode()
            }}>
              <Typography variant="body2" color="text.secondary">
                Enter your name and mobile number. We will text you a code by SMS to check the number is yours.
              </Typography>
              <Stack direction="row" spacing={1.5}>
                <TextField
                  select
                  label="Title"
                  value={title}
                  onChange={(event) => setTitle(event.target.value)}
                  sx={{ width: 120 }}
                >
                  {['Mr.', 'Mrs.', 'Ms.', 'Dr.', 'M/s.'].map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  fullWidth
                  label="Your name"
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                  autoComplete="name"
                  required
                />
              </Stack>
              <TextField
                label="Mobile number"
                value={mobile}
                onChange={(event) => setMobile(event.target.value)}
                autoComplete="tel"
                required
              />
              <Button type="submit" variant="contained" disabled={busy || !hasMobile(mobile) || !name.trim()}>
                {busy ? 'Sending…' : 'Send code'}
              </Button>
            </Stack>
          ) : (
            <Stack spacing={2} component="form" onSubmit={(event) => {
              event.preventDefault()
              if (code.trim() && password && confirm) void finish()
            }}>
              <Typography variant="body2" color="text.secondary">
                A code was texted by SMS to {sentTo || 'your mobile'}. It is valid for a few minutes. If you do not get
                it, check the number and try again.
              </Typography>
              <TextField
                label="Code"
                value={code}
                onChange={(event) => setCode(event.target.value)}
                inputProps={{ inputMode: 'numeric', 'aria-label': 'Code' }}
                autoFocus
                required
              />
              <TextField
                label="Choose a password"
                type="password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                autoComplete="new-password"
                helperText="At least 8 characters"
                required
              />
              <TextField
                label="Type it again"
                type="password"
                value={confirm}
                onChange={(event) => setConfirm(event.target.value)}
                autoComplete="new-password"
                required
              />
              <Button type="submit" variant="contained" disabled={busy || !code.trim() || !password || !confirm}>
                {busy ? 'Creating…' : 'Create account'}
              </Button>
              <Button onClick={() => setStep('number')} disabled={busy}>
                Use a different number
              </Button>
            </Stack>
          )}

          <Button sx={{ mt: 2 }} onClick={() => navigate('/my/login')}>
            Back to sign in
          </Button>
        </CardContent>
      </Card>
    </Box>
  )
}
